import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramPumpLawsWaterHP } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.hydronicWaterHp.v1';

type Fields = {
  flowRate: string;
  head: string;
  specificGravity: string;
  waterHorsepower: string;
};
const EMPTY: Fields = { flowRate: '', head: '', specificGravity: '', waterHorsepower: '' };

/**
 * Hydronics — Water Horsepower
 *   WHP = (GPM × Head × SG) / 3960
 */
const HydronicPumpLawsWaterHorsepowerScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const gpm = toImp(fields.flowRate, 'waterFlow');
    const h = toImp(fields.head, 'head');
    const sg = parseFloat(fields.specificGravity);
    const whp = toImp(fields.waterHorsepower, 'power');

    if (fields.waterHorsepower === '') {
      setSolvedFor('waterHorsepower');
      set({ waterHorsepower: fromImp((gpm * h * sg) / 3960, 'power', 3) });
    } else if (fields.flowRate === '') {
      if (!(h > 0) || !(sg > 0)) { setError('Head and Specific Gravity cannot be zero.'); return; }
      setSolvedFor('flowRate');
      set({ flowRate: fromImp((whp * 3960) / (h * sg), 'waterFlow', 2) });
    } else if (fields.head === '') {
      if (!(gpm > 0) || !(sg > 0)) { setError('Flow Rate and Specific Gravity cannot be zero.'); return; }
      setSolvedFor('head');
      set({ head: fromImp((whp * 3960) / (gpm * sg), 'head', 2) });
    } else if (fields.specificGravity === '') {
      if (!(gpm > 0) || !(h > 0)) { setError('Flow Rate and Head cannot be zero.'); return; }
      setSolvedFor('specificGravity');
      set({ specificGravity: ((whp * 3960) / (gpm * h)).toFixed(3) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.flowRate && { label: 'Flow Rate', value: fields.flowRate, unit: unit('waterFlow'), emphasis: solvedFor === 'flowRate' },
    fields.head && { label: 'Total Head', value: fields.head, unit: unit('head'), emphasis: solvedFor === 'head' },
    fields.specificGravity && { label: 'Specific Gravity', value: fields.specificGravity, emphasis: solvedFor === 'specificGravity' },
    fields.waterHorsepower && { label: 'Water Power', value: fields.waterHorsepower, unit: unit('power'), emphasis: solvedFor === 'waterHorsepower' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Water Horsepower"
      subtitle="Calculate water horsepower for pump performance"
      formula={
        <>
          <FormulaLine>{isImperial ? 'WHP = (GPM × Head × SG) / 3960' : 'WkW = (L/min × Head × SG) / 6116'}</FormulaLine>
          <FormulaNote>
            Water Power ({unit('power')}), Flow Rate ({unit('waterFlow')}), Head ({unit('head')}), SG = Specific Gravity.
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formula: WHP = (GPM × Head × SG) / 3960' : 'Formula: WkW = (L/min × Head × SG) / 6116'}
      diagram={<DiagramPumpLawsWaterHP />}
      outputSlots={{
        gpm: fields.flowRate ? toImp(fields.flowRate, 'waterFlow') : undefined,
        pump_head_ft: fields.head ? toImp(fields.head, 'head') : undefined,
        specific_gravity: fields.specificGravity ? parseFloat(fields.specificGravity) : undefined,
        water_hp: fields.waterHorsepower ? toImp(fields.waterHorsepower, 'power') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.gpm != null && !fields.flowRate) {
          patch.flowRate = fromImp(values.gpm, 'waterFlow', 2);
        }
        if (values.pump_head_ft != null && !fields.head) {
          patch.head = fromImp(values.pump_head_ft, 'head', 2);
        }
        if (values.specific_gravity != null && !fields.specificGravity) {
          patch.specificGravity = values.specific_gravity.toFixed(3);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Flow Rate (${unit('waterFlow')})`} value={fields.flowRate} onChangeText={(v) => set({ flowRate: v })} rightText={unit('waterFlow')} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Total Head (${unit('head')})`} value={fields.head} onChangeText={(v) => set({ head: v })} rightText={unit('head')} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Specific Gravity" value={fields.specificGravity} onChangeText={(v) => set({ specificGravity: v })} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Water Power (${unit('power')})`} value={fields.waterHorsepower} onChangeText={(v) => set({ waterHorsepower: v })} rightText={unit('power')} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default HydronicPumpLawsWaterHorsepowerScreen;
